import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs hover:bg-gray-50"
      >
        {label}
        {selected.length > 0 && ` (${selected.length})`}
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-60 min-w-[200px] overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {options.map((o) => {
            const isSelected = selected.includes(o.value)
            return (
              <label key={o.value} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() =>
                    onChange(isSelected ? selected.filter((x) => x !== o.value) : [...selected, o.value])
                  }
                />
                <span className="truncate">{o.label}</span>
              </label>
            )
          })}
          {selected.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full border-t border-gray-200 px-3 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}
